import { useSyncExternalStore } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { ColorMode } from '@/theme';

export type ColorModePreference = ColorMode | 'system';

interface ColorModeState {
  preference: ColorModePreference;
  setPreference: (preference: ColorModePreference) => void;
  /** Cycles dark → light → system, the order the header toggle steps through. */
  cycle: () => void;
}

export const STORAGE_KEY = 'tgbi.color-mode';

/**
 * Dark, for anyone who has not said otherwise.
 *
 * Not the operating system's answer: this app is read at night, in bed, about
 * a town somebody is annoyed with, and dark is what it was designed in. A
 * reader who prefers light says so once and is remembered for ever — the
 * choice is persisted, so this default only ever applies to a first visit.
 *
 * `system` is still reachable from the toggle for anyone who wants the app to
 * follow their machine. It is simply not the starting point.
 */
export const DEFAULT_PREFERENCE: ColorModePreference = 'dark';

export const useColorModeStore = create<ColorModeState>()(
  persist(
    (set, get) => ({
      preference: DEFAULT_PREFERENCE,
      setPreference: (preference) => set({ preference }),
      cycle: () => {
        // Dark first, so the first press from the default lands on light —
        // the one change somebody on the default is likely to want. Starting
        // at `system` would make that press do nothing visible on a machine
        // already set to dark.
        const order: ColorModePreference[] = ['dark', 'light', 'system'];
        const index = order.indexOf(get().preference);
        set({ preference: order[(index + 1) % order.length] ?? DEFAULT_PREFERENCE });
      },
    }),
    { name: STORAGE_KEY },
  ),
);

const QUERY = '(prefers-color-scheme: dark)';

function subscribeToSystem(onChange: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;

  const media = window.matchMedia(QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(QUERY).matches;
}

/**
 * Reads the OS preference. `useSyncExternalStore` rather than an effect so the
 * very first render already has the right value — an effect would paint light
 * mode for one frame before correcting itself, which is the flash everyone
 * notices.
 */
export function useSystemPrefersDark(): boolean {
  return useSyncExternalStore(subscribeToSystem, systemPrefersDark, () => false);
}

/** The mode actually rendered, after resolving 'system' against the OS. */
export function useResolvedColorMode(): ColorMode {
  const preference = useColorModeStore((state) => state.preference);
  const prefersDark = useSystemPrefersDark();

  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

/**
 * The same resolution, without React — for the inline script in index.html
 * that colours the loading shell before any of this has loaded.
 *
 * Kept here rather than only in the HTML so the two cannot drift: if the
 * default or the storage key changes, this is the file both read.
 */
export function resolvePreference(
  preference: ColorModePreference,
  systemPrefersDark: boolean,
): ColorMode {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}
